<template>
  <div class="w-full">
    <div class="w-max mx-auto mb-8 border-b-2 border-transparent">
      <!-- Tabs -->
      <ul class="list-none flex justify-between items-center">
        <li
          v-for="(tab, index) in tabs"
          :key="index"
          :class="
            index === activeTabIndex
              ? 'border-b-2 border-bubblegum'
              : 'opacity-40 hover:opacity-100 duration-300'
          "
          class="cursor-pointer"
        >
          <button
            type="button"
            class="w-full h-full p-4 text-base leading-5 font-bold text-ocean-950"
            @click="setActiveTab(tab.name, index)"
          >
            {{ tab.name }}
          </button>
        </li>
      </ul>
    </div>

    <!-- Comparison cards -->
    <div class="lg:max-w-max mx-auto flex flex-col md:flex-row justify-center gap-8">
      <div class="p-10 shadow-md rounded-3xl bg-white lg:w-md lg:h-xs">
        <div class="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-2xl flex justify-center items-center bg-ocean-950">
          <img
            :src="vetsterLogo"
            alt="Vetster"
            class="w-9/12 object-contain mt-1"
          />
        </div>
        <div class="mt-6">
          <h3 class="font-title text-2xl leading-6 font-bold text-ocean-950 mb-4">
            <span class="text-bubblegum">
              {{ activeTabContent[0].title }}
            </span>
            {{ activeTabName.toLowerCase() }}
          </h3>
          <ul class="max-w-xs">
            <li
              v-for="tabListItem in activeTabContent[0].listItems"
              :key="tabListItem"
              class="flex items-start gap-4 mb-2"
            >
              <fa
                :icon="['fal', 'check']"
                class="text-2xl text-bubblegum"
              />
              <p class="text-base leading-5 text-neutral-900">
                {{ tabListItem }}
              </p>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex flex-row md:flex-col justify-between items-center gap-4">
        <div class="flex-1 h-px md:w-px bg-neutral-200" />
        <span class="lowercase flex border border-ocean-100 bg-ocean-50 justify-center items-center w-9 h-9 rounded-full text-sm leading-5 font-semibold text-ocean-950">
          {{ $t('for-vets.vs') }}
        </span>
        <div class="flex-1 h-px md:w-px bg-neutral-200" />
      </div>
      <div class="p-10 shadow-md rounded-3xl bg-ocean-50 border border-ocean-100 lg:w-md lg:h-xs">
        <div class="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-2xl flex justify-center items-center bg-ocean-100">
          <fa
            :icon="['fal', activeTabContent[1].icon]"
            class="text-2xl md:text-4xl text-ocean-800"
          />
        </div>
        <div class="mt-6">
          <h3 class="font-title text-2xl leading-6 font-bold text-ocean-950 mb-4">
            {{ activeTabContent[1].title }}
          </h3>
          <ul class="max-w-xs">
            <li
              v-for="tabListItem in activeTabContent[1].listItems"
              :key="tabListItem"
              class="flex items-start gap-4 mb-2"
            >
              <fa
                :icon="['fal', 'xmark']"
                class="text-2xl text-ocean-950"
              />
              <p class="text-base leading-5 text-neutral-900">
                {{ tabListItem }}
              </p>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAssetUrl } from '@/helpers/url';

export default {
  name: 'VetsterVersusCompetitorsShowcase',

  data() {
    return {
      activeTabIndex: 0,
      activeTabName: this.$t('for-vets.video-calls-tab.name'),
      tabs: [
        {
          name: this.$t('for-vets.video-calls-tab.name'),
        },
        {
          name: this.$t('for-vets.platform-tab.name'),
        },
        {
          name: this.$t('for-vets.virtual-care-tab.name'),
        },
      ],
      vetsterLogo: getAssetUrl('images/vetster-logo-accent-pink.svg'),
    };
  },

  computed: {
    activeTabContent() {
      switch (this.activeTabIndex) {
        case 0:
          return [
            {
              title: 'Vetster',
              listItems: [
                this.$t('for-vets.video-calls-tab.in-call-file-sharing'),
                this.$t('for-vets.video-calls-tab.secure-private-communication'),
                this.$t('for-vets.video-calls-tab.no-post-appointment-administration'),
              ],
            },
            {
              icon: 'video',
              title: this.$t('for-vets.video-calls-tab.other-video-platforms'),
              listItems: [
                this.$t('for-vets.video-calls-tab.no-features-for-veterinary-care'),
                this.$t('for-vets.video-calls-tab.no-tools-to-deliver-effective-pet-care'),
              ],
            },
          ];
        case 1:
          return [
            {
              title: "Vetster's",
              listItems: [
                this.$t('for-vets.platform-tab.purpose-built-for-veterinarians'),
                this.$t('for-vets.platform-tab.accessible-anywhere-anytime'),
                this.$t('for-vets.platform-tab.integrated-rx-features'),
              ],
            },
            {
              icon: 'computer-classic',
              title: this.$t('for-vets.platform-tab.on-premise-clinic-software'),
              listItems: [
                this.$t('for-vets.platform-tab.installation-and-ongoing-maintenance'),
                this.$t('for-vets.platform-tab.expensive-subscriptions'),
                this.$t('for-vets.platform-tab.limited-customer-support'),
              ],
            },
          ];
        case 2:
          return [
            {
              title: 'Vetster',
              listItems: [
                this.$t('for-vets.virtual-care-tab.no-contracts'),
                this.$t('for-vets.virtual-care-tab.complete-control-and-flexibility'),
                this.$t('for-vets.virtual-care-tab.deliver-effective-telehealth'),
              ],
            },
            {
              icon: 'briefcase-medical',
              title: this.$t('for-vets.virtual-care-tab.other-virtual-care-services'),
              listItems: [
                this.$t('for-vets.virtual-care-tab.require-an-employee-or-pt-contract'),
                this.$t('for-vets.virtual-care-tab.capped-revenue-opportunities'),
                this.$t('for-vets.virtual-care-tab.restrictions-in-ability-to-provide-services'),
              ],
            },
          ];
        default:
          return [];
      }
    },
  },

  methods: {
    setActiveTab(name, index) {
      if (this.activeTabIndex === index) return;
      this.activeTabName = name;
      this.activeTabIndex = index;
    },
  },
};
</script>
